/**
 * Regelversion 4: Verwaltung und Vollzug.
 *
 * Personal in den Ministerien (Stellenplan) und die Digitalisierung der Verwaltung bestimmen,
 *  - wie viele Vorhaben eure Ministerien gleichzeitig betreuen können,
 *  - wie schnell ein Referentenentwurf fertig wird,
 *  - wie schnell und wie vollständig ein Gesetz nach dem Inkrafttreten wirkt (Vollzug),
 *  - wie genau sich die Wirkung vorher abschätzen lässt (Prognose).
 * Bundestag und Bundesrat werden dadurch nicht schneller.
 *
 * Detailgrad (Einstellung `adm`, Voreinstellung nach Schwierigkeit):
 *   0 = automatisch: kein Stellenplan, Digitalisierung nur über Gesetze, fest 4 Vorhaben
 *   1 = einfach: ein Stellenplan für alle eigenen Ministerien, Investitionen in Digitalisierung
 *   2 = detailliert: Stellenplan je Ministerium, Beamte oder Angestellte, Besoldung, Hilfe für Länder
 * Im Mehrspieler gilt immer 0.
 *
 * Alles hier wirkt nur ab Version 4 (`since(4)`).
 */
import { RESS, RID } from '../content/ressorts';
import { CAP } from '../content/consts';
import { CF, DI } from '../content/settings';
import { S, log } from './state';
import { clamp, de } from './util';
import { since } from './version';

export type Adm = {
  /** Stellenplan je Ressort: −1 knapp, 0 normal, 1 ausgebaut. */
  lvl: Record<string, number>;
  /** Tatsächliche Besetzung je Ressort (bewegt sich langsam auf den Stellenplan zu). */
  fill: Record<string, number>;
  /** Digitalisierung der Verwaltung, 0 bis 100. */
  dig: number;
  /** Laufende Digitalisierungsprojekte (Investitionen, die noch nicht wirken). */
  pipe: number;
  /** Investition in die Digitalisierung, Mrd €/Jahr. */
  inv: number;
  /** Detailliert: neue Stellen als Beamte (günstiger jetzt, Pensionen später). */
  beamte: boolean;
  /** Detailliert: Besoldungsreform, macht den Staat als Arbeitgeber attraktiver. */
  pay: boolean;
  /** Detailliert: Hilfe für Länder und Kommunen, Mrd €/Jahr. */
  land: number;
  /** Aufgelaufene Pensionslast, Mrd €/Jahr. */
  pens: number;
};

export const DIG0 = 30;
export const INV_MAX = 4, LAND_MAX = 3;
export const LVLN: Record<number, string> = { [-1]: 'knapp', 0: 'normal', 1: 'ausgebaut' };

export const newAdm = (): Adm => ({ lvl: {}, fill: {}, dig: DIG0, pipe: 0, inv: 0, beamte: false, pay: false, land: 0, pens: 0 });

/** Gilt die Verwaltung in dieser Partie? */
export const admOn = (): boolean => since(4) && !!S.adm;
/** Detailgrad 0–2 (im Mehrspieler immer 0). */
export function admMode(): number {
  if (!admOn() || S.mp) return 0;
  const v = (CF() as any).adm;
  return v === true ? 1 : clamp(Math.round(Number(v ?? 1)) || 0, 0, 2);
}
const A = (): Adm => S.adm as Adm;

/** Führt die eigene Partei dieses Ressort? */
export const ownRess = (rid: string): boolean => !!S.min && S.min[rid]?.p === 'YOU';
export const ownRids = (): string[] => RESS.map((r: any) => r.id).filter(ownRess);

/** Stellenplan-Ziel eines Ressorts (fremde Ressorts bleiben normal). */
const target = (rid: string) => (admMode() && ownRess(rid) ? (A().lvl[rid] ?? 0) : 0);
export const fillOf = (rid: string): number => (admOn() ? A().fill[rid] ?? 0 : 0);
/** Durchschnittliche Besetzung der eigenen Ressorts (−1 bis 1). */
export function ownFill(): number { const r = ownRids(); return r.length ? r.reduce((s, x) => s + fillOf(x), 0) / r.length : 0; }
/** Durchschnittliche Besetzung aller Ressorts. */
const allFill = () => RESS.reduce((s: number, r: any) => s + fillOf(r.id), 0) / RESS.length;
/** Höchste erreichbare Besetzung (Fachkräftemangel, nur detailliert). */
export const fillCap = (): number => (admMode() === 2 ? (A().pay ? 1 : .7) : 1);

// ── Kosten ──

/** Personalkosten eines Ressorts (Mrd €/Jahr, relativ zu „normal“). */
export const PERS = .5;
export function staffCost(rid: string): number {
  const f = fillOf(rid), w = RID[rid]?.w ?? 1;
  return f * w * PERS * (f > 0 && A().beamte ? .8 : 1);
}
/** Alle laufenden Kosten der Verwaltung, Mrd €/Jahr (positiv = belastet den Haushalt). */
export function admCost(): number {
  if (!admOn()) return 0;
  const a = A();
  return RESS.reduce((s: number, r: any) => s + staffCost(r.id), 0) + a.pens + a.inv + a.land + (a.pay ? .8 : 0);
}

// ── Wirkung ──

/** Vollzug: wie gut Bund, Länder und Kommunen Gesetze umsetzen (0 bis 1). */
export function vollzug(rid?: string): number {
  if (!admOn()) return .45;
  const a = A();
  return clamp(.25 + a.dig / 100 * .6 + allFill() * .1 + (rid ? fillOf(rid) * .1 : 0) + a.land * .03, .05, 1);
}
/** Datenlage: wie genau sich die Wirkung vorher abschätzen lässt (0 bis 1). */
export function dataQ(rid?: string): number {
  if (!admOn()) return .4;
  return clamp(.2 + A().dig / 100 * .6 + (rid ? fillOf(rid) : allFill()) * .2, 0, 1);
}
/** Monate, über die sich die Wirkung eines Gesetzes entfaltet. */
export const kraftMonths = (rid?: string): number => clamp(Math.round(9 - 7 * vollzug(rid)), 3, 10);
/** Prognose: Anteil der geplanten Wirkung, der wahrscheinlich ankommt (Mitte und Spanne). */
export function forecast(rid?: string): { mid: number; lo: number; hi: number; months: number } {
  const mid = .8 + .4 * vollzug(rid), spread = .7 - .45 * dataQ(rid);
  return { mid, lo: mid - spread / 2, hi: mid + spread / 2, months: kraftMonths(rid) };
}
/** Tatsächliche Umsetzung aus einer Zufallszahl r ∈ [0, 1). */
export const luckOf = (r: number, rid?: string): number => { const f = forecast(rid); return Math.round((f.lo + r * (f.hi - f.lo)) * 100) / 100; };

/** Wie viele Vorhaben eine Partei gleichzeitig betreuen kann. */
export function capOf(party = 'YOU'): number {
  const m = admMode(); if (!m || party !== 'YOU') return CAP;
  const f = ownFill();
  if (f <= -.5) return CAP - 1;
  if (m === 2 && f >= .9) return CAP + 2;
  return f >= (m === 2 ? .5 : .66) ? CAP + 1 : CAP;
}
/** Änderung der Bearbeitungszeit eines Referentenentwurfs in Monaten. */
export function draftShift(rid: string): number {
  if (!admOn()) return 0;
  const f = fillOf(rid);
  return f >= .5 ? -1 : f <= -.5 ? 1 : 0;
}
/** Bonus im Bundesrat durch Hilfe für die Länder. */
export const landDeal = (): number => (admOn() && admMode() === 2 ? Math.min(.06, A().land * .02) : 0);

// ── Monatlich ──

export function admTick() {
  if (!admOn()) return;
  const a = A(), m = admMode(), cap = fillCap();
  for (const r of RESS) {
    const id = r.id, t = Math.min(target(id), cap), f = a.fill[id] ?? 0;
    const up = m === 2 ? (a.pay ? .12 : .07) : .34, down = m === 2 ? .2 : .34;
    const nf = f < t ? Math.min(t, f + up) : Math.max(t, f - down);
    a.fill[id] = Math.round(nf * 1000) / 1000;
    if (nf > 0 && a.beamte) a.pens += nf * (r.w || 1) * PERS * .1 / 12;
  }
  a.pens *= .998;
  // Digitalisierung: Investitionen wirken mit Verzögerung, ohne IT-Personal nur halb
  const it = ownFill() < -.5 ? .5 : 1;
  if (m >= 1) a.pipe += (a.inv * .2 + (m === 2 ? a.land * .1 : 0)) * it;
  const done = a.pipe / 12; a.pipe -= done;
  a.dig = clamp(a.dig + done - .04, 0, 100);
  // Bürger merken die Verwaltung: Wartezeiten bei knappem Personal, bessere Ämter bei guter Digitalisierung
  const z = (a.dig - DIG0) * .001 + Math.min(0, ownFill()) * .035;
  for (const g in S.bonus) S.bonus[g] += z + (g === 'unt' ? (a.dig - DIG0) * .0006 : 0);
}

/** Ein Digitalisierungsgesetz tritt in Kraft. */
export function admOnEnact(L: any) {
  if (!admOn() || L.rep || L.bestand) return;
  if (L.iss === 'digital') { A().pipe += 10 * (L.q || 1); }
}

// ── Spielzüge ──

export function admSet(rid: string, v: number) {
  const m = admMode(); if (!m || S.phase !== 'govern') return;
  const lv = clamp(Math.round(v), -1, 1);
  if (rid === 'all') { for (const r of ownRids()) A().lvl[r] = lv; log(`Stellenplan für eure Ministerien: ${LVLN[lv]}.`); return; }
  if (m < 2 || !ownRess(rid)) return;
  A().lvl[rid] = lv; log(`Stellenplan ${RID[rid].n}: ${LVLN[lv]}.`);
}
export function admInv(v: number) { if (admMode() < 1 || S.phase !== 'govern') return; A().inv = clamp(Math.round(v * 2) / 2, 0, INV_MAX); }
export function admLand(v: number) { if (admMode() < 2 || S.phase !== 'govern') return; A().land = clamp(Math.round(v * 2) / 2, 0, LAND_MAX); }
export function admOpt(k: 'beamte' | 'pay', on: boolean) {
  if (admMode() < 2 || S.phase !== 'govern') return;
  A()[k] = !!on;
  if (k === 'pay' && on) log('Besoldungsreform: Der Bund zahlt mehr, offene Stellen lassen sich leichter besetzen.');
}

/** Kurzbeschreibung für Anzeigen. */
export const digLabel = (d: number) => (d < 25 ? 'Papier und Fax' : d < 45 ? 'Flickenteppich' : d < 65 ? 'im Aufbau' : d < 85 ? 'weitgehend digital' : 'digitaler Staat');
export const pct = (x: number) => `${Math.round(x * 100)} %`;
export const costText = (x: number) => `${x >= 0 ? '' : '−'}${de(Math.abs(x), 1)} Mrd €/Jahr`;
/** Schwierigkeit macht Pannen teurer. */
export const panMult = (): number => [.6, 1, 1.4][DI()];
